'use client'

import useHardSignOut from '@/hooks/useHardSignOut'
import { ArrowLeftOnRectangleIcon, LockClosedIcon } from '@heroicons/react/24/outline'
import type { Portal } from '@/lib/subdomain'

/**
 * Shown when a signed-in user lands on a standalone portal host they are not
 * authorized for — e.g. a non-operator on admin.42a.ai, or a non-partner on
 * partners.42a.ai. Access to these hosts is gated purely by role (operator /
 * partner), independent of organization membership, so this is the terminal
 * state for the wrong audience. The only action is to sign out.
 *
 * Deliberately does NOT link across hosts: a user without portal access has no
 * business being redirected deeper into 42A infrastructure from here.
 *
 * `reason` only changes the copy — the denial itself is decided by the backend
 * (resolveEffectivePartner), never by this prop.
 */
export default function PortalAccessDeniedScreen({
  portal,
  reason = 'not-authorized',
}: {
  portal: Portal
  /**
   * 'partner-disabled' — the user IS attached to a partner, but a 42a operator
   * switched that partner off, so the whole partner team loses the portal.
   * Saying so beats the misleading "not associated with a partner".
   */
  reason?: 'not-authorized' | 'partner-disabled'
}) {
  const handleSignOut = useHardSignOut()

  const isPartnerDisabled = portal === 'partners' && reason === 'partner-disabled'

  const heading = isPartnerDisabled
    ? 'Partner access disabled'
    : portal === 'admin'
      ? 'Operator access required'
      : 'Partner access required'
  const body = isPartnerDisabled
    ? 'Access to the 42A partner portal has been disabled for your partner account. Your organizations are unaffected — contact your 42A representative to restore portal access.'
    : portal === 'admin'
      ? 'This is the 42A operator console. Your account is not a 42A operator, so you cannot access this portal.'
      : 'This is the 42A partner portal. Your account is not associated with a partner, so you cannot access this portal.'

  return (
    <div
      className="flex min-h-screen items-center justify-center px-6 py-12"
      style={{ background: 'var(--color-background)' }}
    >
      <div
        className="w-full max-w-md rounded-2xl border p-8 shadow-xl"
        style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
      >
        <div className="mb-6 flex justify-center">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-full"
            style={{ background: 'var(--color-surface-hover)' }}
          >
            <LockClosedIcon className="h-7 w-7" style={{ color: 'var(--color-text-muted)' }} />
          </div>
        </div>

        <h1
          className="mb-2 text-center text-xl font-semibold"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {heading}
        </h1>
        <p
          className="mb-6 text-center text-sm leading-relaxed"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {body}
        </p>

        <div className="my-4 h-px" style={{ background: 'var(--color-border)' }} />

        <button
          type="button"
          onClick={handleSignOut}
          className="flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-[var(--color-surface-hover)]"
          style={{ color: 'var(--color-text-primary)' }}
        >
          <ArrowLeftOnRectangleIcon className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </div>
  )
}
